/**
 * `RESEQUENCE_DAILY` step. BE `ValidateDailyStops` rejects partial payloads
 * — the caller must pass the full ordered route. Stops are deduped +
 * monotonically renumbered via `buildSequencePairs`. The BE-committed
 * sequence is returned so the verifier reads truth-of-record from the
 * response, not RTK cache.
 */
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { RouteId, ServiceDate } from 'types/entities/shared/ids'
import type {
  DailyStop,
  Deps,
  OnRequestStop,
  PipelineStep,
  SequenceDailyResponse,
} from 'types/modules/routing'
import { isDailyStop, isOnRequestStop } from 'types/modules/routing'

import { buildDailyResequencePayload } from '../payloads'
import type { StepResult } from '../types'

export interface SequenceDailyStepArgs {
  readonly routeId: RouteId
  readonly serviceDate: ServiceDate
  readonly orderedStops: ReadonlyArray<DailyStop | OnRequestStop>
}

export type SequenceDailyStepValue = SequenceDailyResponse

export type SequenceDailyStepResult = StepResult<'RESEQUENCE_DAILY', SequenceDailyStepValue>

const stopWireId = (s: DailyStop | OnRequestStop): string => {
  if (isDailyStop(s) || isOnRequestStop(s)) {
    return String(s.workOrderId ?? s.stopId)
  }
  return ''
}

export const makeSequenceDailyStep = (
  args: SequenceDailyStepArgs,
  deps: Deps,
): PipelineStep<'RESEQUENCE_DAILY', SequenceDailyStepResult> => {
  const stepId = `sequence-daily:${args.routeId}:${args.serviceDate}`
  return {
    id: stepId,
    phase: 'RESEQUENCE_DAILY',
    label: 'Failed to resequence daily schedule',
    applyOrder: 200,
    run: async (ctx) => {
      if (args.orderedStops.length === 0) {
        return ok({
          phase: 'RESEQUENCE_DAILY',
          stepId,
          value: { serviceDate: args.serviceDate, sequence: [] },
        })
      }
      const rows = args.orderedStops.map(s => ({ id: stopWireId(s) }))
      const payload = buildDailyResequencePayload(rows, 'id', { mode: 'strict' })
      if (!payload.ok) return err(payload.error)
      try {
        const response: SequenceDailyResponse = await deps
          .sequenceDaily({
            routeId: args.routeId,
            data: {
              serviceDate: args.serviceDate,
              sequence: payload.value.pairs.map(p => ({
                stopId: String(p.stopId),
                sequence: p.sequence,
              })),
              versionContext: ctx.versionContext,
            },
            skipInvalidate: true,
          })
          .unwrap()
        return ok({ phase: 'RESEQUENCE_DAILY', stepId, value: response })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'RESEQUENCE_DAILY',
          status,
          message,
        })
      }
    },
  }
}
